/**
 * 认证存储服务
 * 提供基于 Cookie 的认证信息管理功能，包括 token、uid 和 device_id 的存储、读取和清除
 */

import { UserAccountStatus, type UserAccountStatusValue } from '@/types/auth';

// ==================== 核心常量 ====================

/**
 * Cookie 键名：认证令牌
 */
const COOKIE_TOKEN_KEY = 'manclaw_token';

/**
 * Cookie 键名：认证令牌绝对过期时间（Unix 毫秒）
 */
const COOKIE_TOKEN_EXPIRES_AT_KEY = 'manclaw_token_expires_at';

/**
 * Cookie 键名：用户 ID
 */
const COOKIE_UID_KEY = 'manclaw_uid';

/**
 * Cookie 键名：团队角色
 */
const COOKIE_ROLE_KEY = 'manclaw_role';

/**
 * Cookie 键名：团队角色集合
 */
const COOKIE_ROLES_KEY = 'manclaw_roles';

/**
 * Cookie 键名：账号状态
 */
const COOKIE_STATUS_KEY = 'manclaw_status';

/**
 * Cookie 键名：设备 ID
 */
const COOKIE_DEVICE_ID_KEY = 'manclaw_device_id';

/**
 * 默认 token 过期天数
 */
const DEFAULT_TOKEN_EXPIRY_DAYS = 7;

/**
 * 设备 ID 过期天数（持久化 365 天）
 */
const DEVICE_ID_EXPIRY_DAYS = 365;

/**
 * Token 剩余不足 1 天时主动续期
 */
const TOKEN_RENEW_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// ==================== 内部辅助函数 ====================

/**
 * 设置 Cookie
 * @param name - Cookie 名称
 * @param value - Cookie 值
 * @param days - 过期天数
 */
function setCookie(name: string, value: string, days: number): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value}; ${expires}; path=/; SameSite=Lax`;
}

/**
 * 读取 Cookie
 * @param name - Cookie 名称
 * @returns Cookie 值，不存在时返回 null
 */
function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  return null;
}

/**
 * 删除 Cookie
 * @param name - Cookie 名称
 */
function deleteCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
}

/**
 * 生成 8 位短 ID（NanoID 风格，36 字符字母表）
 * @returns 8 位随机字符串
 */
function generateShortId(size = 8): string {
  const alphabet = '0123456789abcdefghijklmnopqrstuvwxyz';
  const values = crypto.getRandomValues(new Uint8Array(size));
  let id = '';
  for (let i = 0; i < size; i++) {
    id += alphabet[values[i] % alphabet.length];
  }
  return id;
}

// ==================== 导出函数 ====================

/**
 * 读取账号状态
 * @returns 账号状态值，不存在时返回 null
 */
export function getAuthStatus(): UserAccountStatusValue | null {
  const status = getCookie(COOKIE_STATUS_KEY);
  if (status === '1') return UserAccountStatus.ACTIVE;
  if (status === '0') return UserAccountStatus.DISABLED;
  // 兼容历史冻结状态，统一按禁用处理。
  if (status === '2') return UserAccountStatus.DISABLED;
  return null;
}

/**
 * 读取认证令牌
 * @returns token 值，不存在时返回 null
 */
export function getAuthToken(): string | null {
  return getCookie(COOKIE_TOKEN_KEY);
}

/**
 * 判断当前 Token 是否需要主动续期。
 * 历史登录态没有保存过期时间时，在首次业务请求刷新一次以补齐元数据。
 */
export function shouldRefreshAuthToken(now = Date.now()): boolean {
  if (!getAuthToken()) return false;

  const expiresAt = getCookie(COOKIE_TOKEN_EXPIRES_AT_KEY);
  if (expiresAt === null) return true;

  const parsed = Number(expiresAt);
  return !Number.isFinite(parsed) || parsed <= now + TOKEN_RENEW_THRESHOLD_MS;
}

/**
 * 读取用户 ID
 * @returns 用户 ID（数字），不存在时返回 null
 */
export function getAuthUid(): number | null {
  const uid = getCookie(COOKIE_UID_KEY);
  if (uid === null) {
    return null;
  }
  const parsed = parseInt(uid, 10);
  return isNaN(parsed) ? null : parsed;
}

/**
 * 读取团队角色
 */
export function getAuthRole(): 'admin' | 'leader' | 'member' | 'none' {
  const role = getCookie(COOKIE_ROLE_KEY);
  return role === 'admin' || role === 'leader' || role === 'member' || role === 'none'
    ? role
    : 'none';
}

/**
 * 读取团队角色集合
 */
export function getAuthRoles(): Array<'admin' | 'leader' | 'member'> {
  const roles = getCookie(COOKIE_ROLES_KEY);
  if (!roles) {
    const role = getAuthRole();
    return role === 'admin' || role === 'leader' || role === 'member' ? [role] : [];
  }
  return Array.from(
    new Set(
      roles
        .split(',')
        .map((item) => item.trim())
        .filter(
          (item): item is 'admin' | 'leader' | 'member' =>
            item === 'admin' || item === 'leader' || item === 'member',
        ),
    ),
  );
}

/**
 * 保存认证令牌和用户 ID
 * @param token - 认证令牌
 * @param uid - 用户 ID
 * @param expiresInSeconds - 过期秒数，默认使用 DEFAULT_TOKEN_EXPIRY_DAYS
 */
export function saveAuthTokens(
  token: string,
  uid: number,
  expiresInSeconds?: number,
  role: 'admin' | 'leader' | 'member' | 'none' = 'none',
  roles: Array<'admin' | 'leader' | 'member' | 'none'> = [],
  status?: UserAccountStatusValue,
): void {
  let expiryDays = DEFAULT_TOKEN_EXPIRY_DAYS;

  if (expiresInSeconds !== undefined) {
    expiryDays = expiresInSeconds / (24 * 60 * 60);
  }

  setCookie(COOKIE_TOKEN_KEY, token, expiryDays);
  setCookie(
    COOKIE_TOKEN_EXPIRES_AT_KEY,
    String(Date.now() + expiryDays * 24 * 60 * 60 * 1000),
    expiryDays,
  );
  setCookie(COOKIE_UID_KEY, String(uid), expiryDays);
  setCookie(COOKIE_ROLE_KEY, role, expiryDays);
  setCookie(
    COOKIE_ROLES_KEY,
    roles.filter((item) => item === 'admin' || item === 'leader' || item === 'member').join(','),
    expiryDays,
  );
  if (status !== undefined) {
    setCookie(COOKIE_STATUS_KEY, String(status), expiryDays);
  }
}

/**
 * 清除认证令牌和用户 ID，保留设备 ID
 */
export function clearAuthTokens(): void {
  deleteCookie(COOKIE_TOKEN_KEY);
  deleteCookie(COOKIE_TOKEN_EXPIRES_AT_KEY);
  deleteCookie(COOKIE_UID_KEY);
  deleteCookie(COOKIE_ROLE_KEY);
  deleteCookie(COOKIE_ROLES_KEY);
  deleteCookie(COOKIE_STATUS_KEY);
}

/**
 * 获取或创建设备 ID
 * 如果设备 ID 不存在，则生成一个新的并持久化到 Cookie
 * @returns 设备 ID
 */
export function getOrCreateDeviceId(): string {
  let deviceId = getCookie(COOKIE_DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = generateShortId();
    setCookie(COOKIE_DEVICE_ID_KEY, deviceId, DEVICE_ID_EXPIRY_DAYS);
  }

  return deviceId;
}
